import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import type { SvgIconComponent } from '@mui/icons-material';
import { ChatLink } from './ChatLink';
import { linkKindForHeader, pathForCell } from './cellLinks';
import { isNumericColumn, statusPillColors, textWidth, toneForLabel } from './vizTokens';
import type { StatusTone } from './vizTokens';

export type ChatTableAlign = 'left' | 'right' | 'center';

export interface ChatDataTableProps {
  headers: string[];
  rows: string[][];
  /** Optional caption rendered above the grid. */
  title?: string;
  /** Per-column alignment from a GFM delimiter row. Falls back to numeric detection. */
  align?: ChatTableAlign[];
  /**
   * Whether id columns and whole-cell link syntax become links. A chart's table twin
   * passes `false`: it is a redundant view of marks, not a navigation surface.
   */
  linkable?: boolean;
}

/** A whole cell the model wrote as link syntax, despite being told not to. */
const WHOLE_CELL_LINK = /^\[([^\]\n]*)\]\(([^)\s]*)\)$/;

const STATUS_ICONS: Record<StatusTone, SvgIconComponent> = {
  success: CheckCircleRoundedIcon,
  danger: CancelRoundedIcon,
  warning: WarningAmberRoundedIcon,
  info: InfoRoundedIcon,
  neutral: RadioButtonUncheckedRoundedIcon,
};

/**
 * Status wears the reserved scale and always ships an icon *and* a label — colour is
 * never the only channel.
 *
 * The label text is a direct child of the pill, deliberately: wrapping it in another
 * span would make the same string addressable twice in the accessibility tree.
 */
function StatusPill({ text }: { text: string }) {
  const theme = useTheme();
  const tone = toneForLabel(text);
  const { bg, fg } = statusPillColors(theme, tone);
  const Icon = STATUS_ICONS[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.85,
        py: 0.15,
        borderRadius: '999px',
        whiteSpace: 'nowrap',
        fontSize: '0.7rem',
        fontWeight: 700,
        lineHeight: 1.6,
        bgcolor: bg,
        color: fg,
      }}
    >
      <Icon aria-hidden sx={{ fontSize: '0.8rem' }} />
      {text}
    </Box>
  );
}

/** Columns whose values should render as status pills rather than plain text. */
function isStatusColumn(header: string, values: string[]): boolean {
  if (/^(status|state|result|outcome|escalation|route|verdict|decision)$/i.test(String(header ?? '').trim())) {
    return true;
  }
  // Or: at least two non-empty values, all of which map onto a real status tone.
  const vals = values.filter((v) => String(v ?? '').trim() !== '');
  if (vals.length < 2) return false;
  return vals.every((v) => toneForLabel(String(v)) !== 'neutral');
}

/**
 * A real table for tabular data.
 *
 * The assistant emits GitHub-flavoured markdown tables and ```table fences; both land
 * here instead of leaking raw pipes into a paragraph. It is also the table twin every
 * chart ships, so no value is reachable only by hovering a mark.
 */
export function ChatDataTable({
  headers,
  rows,
  title,
  align,
  linkable = true,
}: ChatDataTableProps) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  if (!headers?.length || !rows?.length) return null;

  const columns = headers.map((header, i) => {
    const values = rows.map((r) => String(r[i] ?? ''));
    const status = isStatusColumn(header, values);
    /**
     * Width from the widest value in the column, not a flat 96px.
     *
     * An email is one unbreakable word: squeezed into a flat share of the panel it
     * truncated mid-value (`…@moc…`), which is worse than useless — the operator cannot
     * tell which customer it is. Wide columns instead widen the table and let its own
     * scroller take the overflow, and text still wraps rather than clipping.
     */
    const widest = Math.max(
      textWidth(String(header ?? ''), 11.5),
      ...values.map((v) => textWidth(v, 13)),
    );
    return {
      header,
      status,
      // Precedence: a status column is never an id column. Numeric alignment is
      // decided independently — suppressing it for linked columns would make id
      // columns reflow the moment navigation is enabled.
      link: status || !linkable ? null : linkKindForHeader(header, values),
      numeric: isNumericColumn(values),
      width: Math.round(Math.min(300, Math.max(88, widest + 28))),
    };
  });

  const tableMinWidth = Math.min(
    1400,
    Math.max(240, columns.reduce((sum, c) => sum + c.width, 0)),
  );

  const alignOf = (i: number): ChatTableAlign =>
    align?.[i] ?? (columns[i].numeric ? 'right' : 'left');

  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        my: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px', // literal px: `sx.borderRadius` is theme-multiplied
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {title && (
        <Typography
          component="figcaption"
          variant="caption"
          sx={{
            display: 'block',
            px: 1.25,
            py: 0.85,
            fontWeight: 800,
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.text.primary, isLight ? 0.025 : 0.06),
          }}
        >
          {title}
        </Typography>
      )}
      {/* Wide tables scroll inside their own box; the chat panel never scrolls sideways. */}
      <Box sx={{ maxWidth: '100%', maxHeight: 420, overflowX: 'auto', overflowY: 'auto' }}>
        <Table size="small" sx={{ minWidth: tableMinWidth, tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {columns.map((c, i) => (
                <TableCell
                  key={`h${i}`}
                  scope="col"
                  align={alignOf(i)}
                  sx={{ py: 0.75, px: 1.25, minWidth: `${c.width}px`, whiteSpace: 'nowrap' }}
                >
                  {c.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, ri) => (
              <TableRow
                key={`r${ri}`}
                sx={{
                  '&:nth-of-type(even)': {
                    bgcolor: alpha(theme.palette.text.primary, isLight ? 0.018 : 0.05),
                  },
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, isLight ? 0.05 : 0.12),
                  },
                  '&:last-of-type td': { borderBottom: 0 },
                }}
              >
                {columns.map((c, ci) => {
                  const raw = String(row[ci] ?? '');
                  const inlineLink = linkable ? WHOLE_CELL_LINK.exec(raw.trim()) : null;
                  const path = c.link ? pathForCell(c.link, raw) : null;
                  return (
                    <TableCell
                      key={`c${ri}-${ci}`}
                      align={alignOf(ci)}
                      // Stable hooks for tests and for the numeric treatment below.
                      className={[
                        c.numeric ? 'chat-num-cell' : '',
                        c.status ? 'chat-status-cell' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      sx={{
                        py: 0.6,
                        px: 1.25,
                        // A long unbroken value (an email, a URL-ish ref) wraps onto a
                        // second line instead of being cut mid-string.
                        whiteSpace: c.numeric || c.status ? 'nowrap' : 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        // Tabular figures only where numbers line up vertically.
                        ...(c.numeric
                          ? {
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                              fontVariantNumeric: 'tabular-nums',
                            }
                          : null),
                      }}
                    >
                      {c.status && raw.trim() ? (
                        <StatusPill text={raw} />
                      ) : inlineLink ? (
                        <ChatLink href={inlineLink[2]}>{inlineLink[1]}</ChatLink>
                      ) : path ? (
                        <ChatLink href={path}>{raw}</ChatLink>
                      ) : (
                        raw || '—'
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
